// CreditTable.tsx - Refactored to use AdminTable component
import React, { memo, useCallback, useState } from 'react';
import EditModal from './editCreditModal';
import DeleteButton from './deleteButton';
import { AdminTable, StatusBadge, IconButton } from 'components/admin';
import { ColumnDef } from 'types/admin';

interface CreditConfig {
    id: number;
    name: string;
    date: string;
    status: 'Active' | 'Disabled';
    answer: number;
    request: number;
    invite: number;
    signup: number;
    initialFive: number;
}

interface CreditTableProps {
    credits: CreditConfig[];
    onDelete: (id: number) => Promise<string>;
    loading: boolean;
    onUpdate?: (updatedCredit: CreditConfig) => void;
}

const CreditTable: React.FC<CreditTableProps> = memo(({ credits, onDelete, loading, onUpdate }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCredit, setSelectedCredit] = useState<CreditConfig | null>(null);

    // At least one configuration must stay active, so the sole remaining
    // active configuration can't be deleted. Checked client-side up front so
    // the confirmation dialog never appears for a delete that's guaranteed
    // to be rejected.
    const activeCount = credits.filter((c) => c.status === 'Active').length;

    const openModal = useCallback((credit: CreditConfig) => {
        setSelectedCredit(credit);
        setIsModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedCredit(null);
    }, []);

    // Define table columns
    const columns: ColumnDef<CreditConfig>[] = [
        {
            key: 'name',
            header: 'Configuration Name',
        },
        {
            key: 'date',
            header: 'Created Date',
        },
        {
            key: 'status',
            header: 'Status',
            render: (credit) => <StatusBadge status={credit.status} />,
        },
    ];

    // Custom actions renderer
    const renderActions = (credit: CreditConfig) => (
        <>
            {/* Hidden data for accessibility (preserve from original) */}
                                    <span className="hidden" data-credit={JSON.stringify(credit)}>
                                        {credit.answer}{credit.initialFive}{credit.invite}{credit.request}{credit.signup}
                                    </span>
            
            <IconButton
                variant="edit"
                onClick={() => openModal(credit)}
                                        title="Edit"
            />
                                    <DeleteButton
                                        id={credit.id}
                                        name={credit.name}
                                        onDelete={onDelete}
                                        disabled={loading}
                                        isOnlyActiveConfig={credit.status === 'Active' && activeCount <= 1}
                                    />
        </>
    );

    return (
        <>
            <AdminTable
                columns={columns}
                data={credits}
                loading={loading}
                emptyMessage="No configurations found"
                renderActions={renderActions}
                showSerialNumber={true}
            />

            {selectedCredit && (
                <EditModal
                    isOpen={isModalOpen}
                    credit={selectedCredit}
                    onClose={closeModal}
                    onUpdate={onUpdate}
                />
            )}
        </>
    );
});

CreditTable.displayName = 'CreditTable';
export default CreditTable;
